import './index.less';
import { getStoriesCommentList } from "../../../common/utils/api.js";

import ScrollLoader from '../../../common/utils/scroll-loader';

class StoriesComment {
  constructor(rootEl, options = {}) {
    this.wrapper = rootEl;
    this.bodyWrapper = rootEl.querySelector('.comment-body');
    this.titleId = this.bodyWrapper?.dataset?.titleId;

    // 先检查必要元素
    if (!this.wrapper || !this.titleId || !this.bodyWrapper) {
      return;
    }

    // 默认配置
    this.config = {
      pageSize: 5,
      threshold: 120,
      debounceDelay: 100,
      timeout: 10000,
      retryCount: 3,
      ...options
    };

    // 初始化滚动加载器
    this.initScrollLoader();

    this.init().catch(error => {
      console.error('初始化评论组件失败:', error);
    });
  }

  async init() {
    // 初始化UI元素
    this.commentsContainer = this.bodyWrapper;
    this.commentCount = this.wrapper.querySelector('.comment-num');
    this.loadingEl = this.bodyWrapper.querySelector('#comment-loading');
    this.errorEl = this.bodyWrapper.querySelector('.comment-error');

    // 初始化滚动加载器（包含自动加载第一页数据）
    // 不再需要单独调用 loadInitialComments
  }

  initScrollLoader() {
    if (!this.bodyWrapper) {
      return;
    }

    // 创建滚动加载器实例
    this.scrollLoader = new ScrollLoader({
      container: this.bodyWrapper,
      pageSize: this.config.pageSize,
      threshold: this.config.threshold,
      debounceDelay: this.config.debounceDelay,
      useCache: true,
      cacheKeyPrefix: `${this.titleId}`,

      // 加载数据函数
      loadData: async ({ page, pageSize }) => {
        try {
          const response = await getStoriesCommentList({
            page: page,
            limit: pageSize,
            titleId: this.titleId,
            activeType: 2,
          });

          // 根据API返回格式提取数据
          const result = {
            list: response?.data?.list || [],
            total: response?.data?.totalCount || 0
          };

          return result;
        } catch (error) {
          throw error;
        }
      },

      // 渲染数据函数
      renderData: (list, total) => {
        this.render(list, total);
      },

      // 使用 ScrollLoader 内置的默认加载指示器和错误提示 UI

      // 清空容器回调
      onClear: () => {
        if (this.bodyWrapper) {
          this.bodyWrapper.innerHTML = '';
        }
      }
    });

    // 初始化 ScrollLoader 并自动加载第一页数据
    this.scrollLoader.init();
  }

  // 显示加载状态
  showLoading() {
    if (this.loadingEl) {
      this.loadingEl.style.display = 'block';
    }
  }

  // 隐藏加载状态
  hideLoading() {
    if (this.loadingEl) {
      this.loadingEl.style.display = 'none';
    }
  }

  // 显示错误信息
  showError(message) {
    if (this.errorEl) {
      this.errorEl.textContent = message;
      this.errorEl.style.display = 'block';
    }
  }

  // 隐藏错误信息
  hideError() {
    if (this.errorEl) {
      this.errorEl.style.display = 'none';
    }
  }



  // 启用滚动加载更多数据
  enableScrollLoading() {
    if (this.scrollLoader) {
      // 设置当前页为1，因为已经加载了第一页
      this.scrollLoader.currentPage = 1;
      this.scrollLoader.enable();
    }
  }

  // 加载更多评论数据（滚动触发）
  loadComments() {
    if (this.scrollLoader) {
      this.scrollLoader.load();
    }
  }

  // 重新加载
  reload() {
    if (this.scrollLoader) {
      this.scrollLoader.reload();
    }
  }

  // 清理资源
  cleanup() {
    if (this.scrollLoader) {
      this.scrollLoader.destroy();
    }

    // 清理错误UI元素
    const errorEls = this.bodyWrapper.querySelectorAll('.comment-error');
    errorEls.forEach(el => el.remove());
  }

  // 安全的HTML渲染
  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  render(comments, total) {
    const commentDisplayBox = this.wrapper.querySelector('.stories-comment');
    const commentNumEl = this.wrapper.querySelector('.comment-num');

    // 如果没有评论，隐藏评论区域
    if (!total || total === 0) {
      if (commentDisplayBox) commentDisplayBox.style.display = 'none';
      return;
    }

    // 有评论，显示评论区域、填入总数
    if (commentDisplayBox) commentDisplayBox.style.display = 'block';
    if (commentNumEl) commentNumEl.textContent = total;

    const fragment = document.createDocumentFragment();

    comments.forEach(comment => {
      const el = document.createElement('div');
      el.className = 'comment-items';

      // 安全渲染用户输入内容
      const safeName = this.escapeHtml(comment.name || 'Anonymous');
      const safeComment = this.escapeHtml(comment.comment || '');
      const formattedDate = comment.date;

      el.innerHTML = `
        <div class="comment-item-left">
          <div class="comment-name">${safeName}</div>
          <div class="comment-time">${formattedDate}</div>
        </div>
        <div class="comment-item-right">${safeComment}</div>
      `;
      fragment.appendChild(el);
    });

    this.bodyWrapper.appendChild(fragment);
  }
}

// 全局实例管理
window.StoriesCommentInstances = window.StoriesCommentInstances || [];

// 初始化评论组件
document.addEventListener('DOMContentLoaded', () => {
  const commentBlocks = document.querySelectorAll('.jackery-stories-comment');

  commentBlocks.forEach(el => {
    try {
      const instance = new StoriesComment(el);
      if (instance.cleanup) {
        window.StoriesCommentInstances.push(instance);
      }
    } catch (error) {
      console.error(error);
    }
  })
})

// 页面卸载时清理资源
window.addEventListener('beforeunload', () => {
  window.StoriesCommentInstances.forEach(instance => {
    try {
      if (instance.cleanup) {
        instance.cleanup();
      }
    } catch (error) {
      console.error(error);
    }
  });
  window.StoriesCommentInstances = [];
});

// 导出类供外部使用
if (typeof module !== 'undefined' && module.exports) {
  module.exports = StoriesComment;
} else if (typeof window !== 'undefined') {
  window.StoriesComment = StoriesComment;
}
